import * as moment from 'moment';
import { IWatrRequest } from '@nkr/models/index';
import { WageAndTravelReimbursementRequestStatus } from '@nkr/enums/watr-reimbursement-request-status';
import { isSelfEmployed } from '../utils/employment-type.utils';

export const WEEKLY_CAP = 2000;

export const getDefaultWeeklyWageBase = (data: IWatrRequest): number => {
    if ((data.wageReimbursementAmount ?? 0) > 0) {
        return data.wageReimbursementAmount;
    }

    if (isSelfEmployed(data.employmentType)) {
        return data.calculatedWeeklyPayByYear || 0;
    }

    return data.calculatedWeeklyPayByPayStub
        || data.actualWageReimbursementAmount
        || data.estimatedWageReimbursementAmount
        || 0;
};

export const filterEligibleWeeks = (data: IWatrRequest) =>
    (data.requestWeeks || []).filter(w =>
        w.status > WageAndTravelReimbursementRequestStatus.PendingApproval &&
        w.status < WageAndTravelReimbursementRequestStatus.CenterDeclined
    );

export const hasAnyWageData = (data: IWatrRequest, wageReimbursementAmount: number): boolean => {
    if (!data) return false;

    const hasTax = (data.incomeIrsYear ?? 0) > 0
        || (data.incomeIrsYearGross ?? 0) > 0
        || (data.calculatedWeeklyPayByYear ?? 0) > 0;

    const stubStart = moment(data.incomePayStubStartDate);
    const hasValidStubStart = !!data.incomePayStubStartDate && stubStart.isValid() && stubStart.year() > 1;
    const hasPaystub = hasValidStubStart
        || (data.incomePayStubGross ?? 0) > 0
        || (data.calculatedWeeklyPayByPayStub ?? 0) > 0
        || (data.calculatedBusinessDaysInPayStub ?? 0) > 0;

    const hasReimbursement = (data.actualWageReimbursementAmount ?? 0) > 0
        || (data.wageReimbursementAmount ?? 0) > 0
        || filterEligibleWeeks(data).length > 0
        || (wageReimbursementAmount ?? 0) > 0;

    return hasTax || hasPaystub || hasReimbursement;
};

export const calculateIncomePercentAmount = (amount: number, incomePercentCovered: number): number => {
    if (!amount || isNaN(amount) || amount <= 0) return 0;
    if (!incomePercentCovered || incomePercentCovered <= 0) return amount;
    const percentToCover = 100 - incomePercentCovered;
    return Math.round((amount * (percentToCover / 100)) * 100) / 100;
};

export const calculateAdjustedBaseAmount = (baseAmount: number, incomePercentCovered: number, weeklyCap: number = WEEKLY_CAP): number => {
    if (!baseAmount || isNaN(baseAmount) || baseAmount <= 0) {
        return 0;
    }

    const adjusted = calculateIncomePercentAmount(baseAmount, incomePercentCovered);
    const capped = Math.min(adjusted, weeklyCap);
    return isNaN(capped) ? 0 : capped;
};

export const calculateAdjustedWeeklyReimbursement = (adjustedBaseAmount: number, otherAmount: number, weeklyCap: number = WEEKLY_CAP): number => {
    if (!adjustedBaseAmount || isNaN(adjustedBaseAmount)) {
        return 0;
    }

    const other = otherAmount || 0;
    const result = Math.max(0, Math.round((adjustedBaseAmount - other) * 100) / 100);
    const cappedResult = Math.min(result, weeklyCap);
    return isNaN(cappedResult) ? 0 : cappedResult;
};

export interface IWageComputation {
    filteredWeeks: ReturnType<typeof filterEligibleWeeks>;
    incomePercentCovered: number;
    defaultRawBaseAmount: number;
    adjustedBaseAmount: number;
    isWeeklyOverrideActive: boolean;
    effectiveWeeklyOverride: number;
    adjustedBaseForWeeks: number;
    finalReimbursementTotal: number;
    weeksIncluded: number;
}

export const computeWageModel = (
    data: IWatrRequest,
    weeklyOverrideAmount: number,
    draftOtherAmounts?: Record<number, number>
): IWageComputation => {
    const filteredWeeks = filterEligibleWeeks(data);
    const incomePercentCovered = data.incomePercentCovered || 0;
    const defaultRawBaseAmount = getDefaultWeeklyWageBase(data);

    const defaultRoundedBaseAmount = Math.round(Math.min(defaultRawBaseAmount, WEEKLY_CAP) * 100) / 100;
    const defaultAdjustedBaseAmount = calculateAdjustedBaseAmount(defaultRoundedBaseAmount, incomePercentCovered, WEEKLY_CAP);
    const isWeeklyOverrideActive = weeklyOverrideAmount > 0 && Math.abs(weeklyOverrideAmount - defaultAdjustedBaseAmount) > 0.009;
    const effectiveWeeklyOverride = isWeeklyOverrideActive ? weeklyOverrideAmount : 0;

    const rawBaseForWeeks = effectiveWeeklyOverride > 0 ? effectiveWeeklyOverride : defaultRawBaseAmount;
    const roundedBaseForWeeks = Math.round(Math.min(rawBaseForWeeks, WEEKLY_CAP) * 100) / 100;
    const adjustedBaseForWeeks = calculateAdjustedBaseAmount(roundedBaseForWeeks, incomePercentCovered, WEEKLY_CAP);

    const fallbackWeekly = incomePercentCovered <= 0
        ? (weeklyOverrideAmount || 0)
        : calculateIncomePercentAmount(weeklyOverrideAmount || 0, incomePercentCovered);

    const sumOfWeeklyAdjusted = filteredWeeks.length === 0
        ? fallbackWeekly
        : filteredWeeks.reduce((sum, w) => {
            const other = draftOtherAmounts?.[w.weekRequested] ?? w.otherAmount ?? 0;
            return sum + calculateAdjustedWeeklyReimbursement(adjustedBaseForWeeks, other, WEEKLY_CAP);
        }, 0);

    return {
        filteredWeeks,
        incomePercentCovered,
        defaultRawBaseAmount,
        adjustedBaseAmount: defaultAdjustedBaseAmount,
        isWeeklyOverrideActive,
        effectiveWeeklyOverride,
        adjustedBaseForWeeks,
        finalReimbursementTotal: Math.round(sumOfWeeklyAdjusted * 100) / 100,
        weeksIncluded: filteredWeeks.length > 0 ? filteredWeeks.length : 1
    };
};
